/**
 * EmptyStateView - Create First Studio Screen
 *
 * ✅ LAYOUT FIX: Sizes against Outlet container (not viewport)
 * - Uses h-full/min-h-full instead of h-screen
 * - Adds safe top padding to avoid TopBar overlap
 * - Responsive design with adaptive centering
 *
 * @module views/EmptyStateView
 */

import React, { useState, useRef, useCallback } from 'react';

import {
  Sparkles,
  Upload,
  CheckCircle2,
  Loader2,
  Brain,
  Network,
  MessageSquare,
} from 'lucide-react';
import { generateSmartVaultTitle } from '@/pages/intelligence-studio/utils/vaultTitle';


/**
 * EmptyStateView Component
 *
 * @param {Object} props
 * @param {Function} props.onCreateNotebook - Callback when studio is created
 */
const EmptyStateView = ({ onCreateNotebook }) => {
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      setFiles(prev => [...prev, ...droppedFiles]);
      if (!title && droppedFiles.length > 0) {
        const smartTitle = generateSmartVaultTitle(droppedFiles);
        setTitle(smartTitle);
      }
    },
    [title]
  );

  const handleFileSelect = e => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
    if (!title && selectedFiles.length > 0) {
      const smartTitle = generateSmartVaultTitle(selectedFiles);
      setTitle(smartTitle);
    }
  };

  const handleCreate = async () => {
    if (!title.trim()) return;
    setIsCreating(true);
    try {
      await onCreateNotebook(title, files);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    // ✅ LAYOUT FIX: Use h-full (sizes to Outlet) instead of h-screen (viewport)
    // Added overflow-y-auto for small viewports where content might exceed container
    <div className="h-full w-full overflow-y-auto bg-gradient-to-br from-slate-50 via-white to-amber-50/30 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-900">
      {/* ✅ LAYOUT FIX: 
          - min-h-full ensures content fills available space
          - pt-6 sm:pt-8 adds safe top spacing (never touches TopBar)
          - pb-8 adds bottom breathing room
          - items-start sm:items-center: on tiny heights, align to top; on normal, center
      */}
      <div className="min-h-full w-full flex items-start sm:items-center justify-center px-4 sm:px-6 md:px-8 pt-6 sm:pt-8 pb-8">
        <div className="w-full max-w-2xl">
          {/* Header Section */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 mb-4 sm:mb-6">
              <img
                src="/images/intelligence-studio-logo.png"
                alt="Intelligence Studio"
                className="h-full w-full object-contain"
                loading="eager"
                draggable={false}
              />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2 sm:mb-3 bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent px-4">
              Intelligence Studio
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 dark:text-neutral-400 max-w-md mx-auto px-4">
              Build a new Studio to ingest documents, connect facts and surface strategic
              insights.
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white dark:bg-neutral-800 rounded-xl sm:rounded-2xl shadow-xl border border-slate-200 dark:border-neutral-700 p-4 sm:p-6 md:p-8">
            {/* Studio Title Input */}
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-semibold text-slate-700 dark:text-neutral-300 mb-2">
                Studio Name
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g., Legal Research, Client Documents..."
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-slate-200 dark:border-neutral-700 rounded-lg sm:rounded-xl bg-slate-50 dark:bg-neutral-900 text-slate-900 dark:text-white placeholder-slate-400 focus:border-amber-500 focus:ring-2 sm:focus:ring-4 focus:ring-amber-500/10 transition-all"
              />
            </div>

            {/* File Upload Area */}
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-semibold text-slate-700 dark:text-neutral-300 mb-2">
                Documents
              </label>
              <div
                onDragOver={e => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-lg sm:rounded-xl p-6 sm:p-8 md:p-12 transition-all cursor-pointer group ${
                  isDragging
                    ? 'border-slate-900 bg-slate-900/5 dark:bg-slate-900/10 scale-[1.01] sm:scale-[1.02]'
                    : files.length > 0
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-slate-300 dark:border-neutral-700 hover:border-slate-900 hover:bg-slate-900/5 dark:hover:bg-slate-900/10'
                }`}
              >
                {files.length > 0 ? (
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-3 sm:mb-4">
                      <CheckCircle2 className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white mb-1">
                      {files.length} {files.length === 1 ? 'file' : 'files'} selected
                    </p>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setFiles([]);
                      }}
                      className="text-xs sm:text-sm text-slate-500 dark:text-neutral-400 hover:text-slate-700 dark:hover:text-neutral-200 mt-2"
                    >
                      Clear selection
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <div
                      className={`inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full mb-3 sm:mb-4 transition-colors ${
                        isDragging
                          ? 'bg-slate-900/10 dark:bg-slate-900/20'
                          : 'bg-slate-100 dark:bg-neutral-700 group-hover:bg-slate-900/5 dark:group-hover:bg-slate-900/10'
                      }`}
                    >
                      <Upload
                        className={`h-6 w-6 sm:h-8 sm:w-8 transition-colors ${
                          isDragging
                            ? 'text-slate-900 dark:text-slate-100'
                            : 'text-slate-400 dark:text-neutral-500 group-hover:text-slate-900 dark:group-hover:text-slate-100'
                        }`}
                      />
                    </div>
                    <p className="text-sm sm:text-base font-medium text-slate-700 dark:text-neutral-300 mb-1 sm:mb-2">
                      {isDragging ? 'Drop files here' : 'Drag & drop files or click to browse'}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-neutral-400 px-2">
                      PDF, Word (.docx), Excel, CSV, and images
                    </p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.tiff,.tif"
                />
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={handleCreate}
              disabled={!title.trim() || isCreating}
              onMouseEnter={() => setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
              style={{
                background: isButtonHovered
                  ? 'linear-gradient(135deg, #0f172a, #1e293b, #020617)'
                  : 'linear-gradient(135deg, #1e293b, #0f172a, #020617)',
                boxShadow: isButtonHovered
                  ? 'inset 0 1px 0 rgba(255,255,255,0.08), 0 10px 26px rgba(15,23,42,0.3)'
                  : 'inset 0 1px 0 rgba(255,255,255,0.08), 0 10px 26px rgba(15,23,42,0.3)',
                border: '1px solid rgba(255,255,255,0.1)',
                transform: isButtonHovered ? 'translateY(-1px)' : 'translateY(0)',
                transition: 'background 300ms ease, box-shadow 300ms ease, transform 150ms ease',
              }}
              className="w-full py-3 sm:py-4 text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3 active:scale-[0.98]"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                  <span className="hidden sm:inline">Creating your studio...</span>
                  <span className="sm:hidden">Creating...</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline">Create Intelligence Studio</span>
                  <span className="sm:hidden">Create Studio</span>
                </>
              )}
            </button>

            {/* Features Preview */}
            <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-200 dark:border-neutral-700">
              <p className="text-xs font-semibold text-slate-500 dark:text-neutral-400 uppercase tracking-wider mb-3 sm:mb-4 text-center sm:text-left">
                What you'll get
              </p>
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 mb-1 sm:mb-2">
                    <Brain className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <p className="text-xs font-medium text-slate-700 dark:text-neutral-300">
                    AI Analysis
                  </p>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 mb-1 sm:mb-2">
                    <Network className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <p className="text-xs font-medium text-slate-700 dark:text-neutral-300">
                    Knowledge Graph
                  </p>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-green-100 dark:bg-green-900/30 mb-1 sm:mb-2">
                    <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <p className="text-xs font-medium text-slate-700 dark:text-neutral-300">
                    Smart Chat
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmptyStateView;
